
import React, { useEffect } from 'react';
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';
import LoadingSpinner from '@/components/itinerary/LoadingSpinner';
import { useTripDetails } from './hooks/useTripDetails';
import TripHeader from './layout/TripHeader';
import TripNavTabs from './layout/TripNavTabs';

const TripLayout: React.FC = () => {
  const { tripId } = useParams<{ tripId: string }>();
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = location.pathname.split('/').pop() || 'itinerary';
  
  // Use our custom hook for fetching trip details
  const { trip, isLoading, isAuthorized } = useTripDetails(tripId, user);
  
  // Redirect from 'fun' tab to 'itinerary'
  useEffect(() => {
    if (currentTab === 'fun' && tripId) {
      navigate(`/trips/${tripId}/itinerary`, { replace: true });
    }
  }, [currentTab, tripId, navigate]);
  
  if (isLoading || authLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen pt-28">
        <LoadingSpinner />
        <p className="ml-2 text-gray-600">Loading trip...</p>
      </div>
    );
  }
  
  if (!trip) {
    return (
      <div className="container mx-auto px-4 py-12 pt-28 text-center">
        <h2 className="text-2xl font-bold mb-4">Trip not found</h2>
        <p className="mb-6">The trip you're looking for doesn't exist or you don't have access to it.</p>
        <button 
          onClick={() => navigate('/trips')} 
          className="bg-glowingTeal text-white px-4 py-2 rounded"
        >
          Back to My Trips
        </button>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#E9ECEF] trip-layout-container">
      <div className="container mx-auto px-4">
        {/* Trip header section */}
        <TripHeader trip={trip} isAuthorized={isAuthorized} />
        
        {/* Navigation tabs */}
        <TripNavTabs 
          currentTab={currentTab} 
          onTabChange={navigate} 
          tripId={tripId || ''}
        />
        
        {/* Main content area */}
        <div className="bg-white rounded-lg shadow-md p-6 my-[87px]">
          <Outlet context={{ trip, isAuthorized }} />
        </div>
      </div>
    </div>
  );
};

export default TripLayout;
